import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  IDashboardCreateMutation,
  IDashboardCreateMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { CREATE_DASHBOARD } from '../../../utils/apollo/dashboards';
import { assertNotNil } from '../../../utils/types';
import { appRoutes } from '../../../path';
import { useAppDispatch } from '../../../redux/hooks';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { dashboardCreated } from '../../../utils/analytics/events/dashboard/events';
import { reloadDashboardTemplates } from '../../../redux/slices/dashboards/dashboardInstance';

const useCreateDashboard = (shouldReplaceNavigation: boolean) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const handleMutationComplete = ({
    dashboardCreate,
  }: IDashboardCreateMutation) => {
    const { entity, errors } = dashboardCreate;
    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);

    dispatch(reloadDashboardTemplates());

    navigate(appRoutes.dashboard.generateLink(entity.baseId), {
      replace: shouldReplaceNavigation,
    });
  };

  const [runCreateDashboard, { loading }] = useMutation<
    IDashboardCreateMutation,
    IDashboardCreateMutationVariables
  >(CREATE_DASHBOARD, {
    onCompleted: handleMutationComplete,
  });

  const createDashboard = useCallback(async () => {
    const newDashboard = await runCreateDashboard({
      variables: {
        input: {
          name: 'Untitled Dashboard',
          analyses: [],
          description: '',
        },
      },
    });

    if (newDashboard.data?.dashboardCreate.entity) {
      track(dashboardCreated(newDashboard.data.dashboardCreate.entity));
    }
  }, [runCreateDashboard, track]);

  return {
    createDashboard,
    loading,
  };
};

export default useCreateDashboard;
